import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Users,
  FileText,
  Ticket,
  // BarChart3,
  MapPin,
  Clock,
  ArrowRight,
  Eye,
} from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { selectUser } from "../../store/slices/authSlice";
import { useEffect } from "react";
import { useState } from "react";
import { toast } from "react-toastify";
import dashboardService from "../../api/Dashboard";
import Loader from "../misc/Loader";
import eventService from "../../api/EventService";

export default function EventsMediaDashboard() {
  const [stats, setStats] = useState([
    {
      title: "Total Events",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Calendar,
      color: "bg-emerald-500",
      onClick: () => navigate("/admin/media/events"),
    },
    {
      title: "Active Events",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Ticket,
      color: "bg-blue-500",
      onClick: () => navigate("/admin/media/events"),
    },
    {
      title: "Conferences",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: FileText,
      color: "bg-purple-500",
      onClick: () => navigate("/admin/media/conference"),
    },
    {
      title: "Total Attendees",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Users,
      color: "bg-orange-500",
    },
  ]);
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [response, recentEventsResponse] = await Promise.all([
          dashboardService.getMediaAndEventsDashboardStats(),
          eventService.getEvents(),
        ]);
        
        // Handle dashboard stats response
        if (response?.data?.status_code === 200) {
          const data = response?.data?.data;
          
          setStats([
            {
              title: "Total Events",
              value: data?.other_events,
              change: "0%",
              changeType: "neutral",
              icon: Calendar,
              color: "bg-emerald-500",
              onClick: () => navigate("/admin/media/events"),
            },
            {
              title: "Upcoming Events",
              value: data?.pending_events,
              change: "0%",
              changeType: "neutral",
              icon: Ticket,
              color: "bg-blue-500",
              onClick: () => navigate("/admin/media/events"),
            },
            {
              title: "Conferences",
              value: data?.conferences,
              change: "0%",
              changeType: "neutral",
              icon: FileText,
              color: "bg-purple-500",
              onClick: () =>
                navigate("/admin/media/conferences-trade-missions"),
            },
            {
              title: "Trade Missions",
              value: data?.trade_missions,
              change: "0%",
              changeType: "neutral",
              icon: Users,
              color: "bg-orange-500",
            },
          ]);
        }
        
        // Handle events response - support both response formats
        let eventsData = null;
        if (recentEventsResponse?.data?.status_code === 200) {
          // Raw axios response format
          eventsData = recentEventsResponse?.data?.data;
        } else if (recentEventsResponse?.status && recentEventsResponse?.data) {
          // Wrapped service response format
          eventsData = recentEventsResponse.data;
        }
        
        if (eventsData && Array.isArray(eventsData)) {
          const filteredEvents = eventsData.filter((event) => {
            if (!event.event_type || !Array.isArray(event.event_type)) {
              return false;
            }
            return (
              !event.event_type.includes("conference") &&
              !event.event_type.includes("trade_mission")
            );
          });
          setRecentEvents(filteredEvents.slice(0, 3));
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred while fetching stats"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [navigate]);

  const quickActions = [
    {
      title: "Create Event",
      icon: Calendar,
      color: "bg-emerald-100 text-emerald-600",
      available: true,
      onClick: () => navigate("/admin/media/events/create"),
    },
    {
      title: "Events",
      icon: Ticket,
      color: "bg-blue-100 text-blue-600",
      available: true,
      onClick: () => navigate("/admin/media/events"),
    },
    {
      title: "Conference & Trade Mission",
      icon: FileText,
      color: "bg-purple-100 text-purple-600",
      available: true,
      onClick: () => navigate("/admin/media/conference"),
    },
    {
      title: "Organizers",
      icon: Users,
      color: "bg-orange-100 text-orange-600",
      available: true,
      onClick: () => navigate("/admin/media/organizer"),
      },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 min-h-screen">
        <Loader fullScreen />
      </div>
    );
  }

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (error) {
      return timeString;
    }
  };

  // Helper function to get event title
  const getEventTitle = (event) => {
    return event.event_title || event.title || "Untitled Event";
  };

  // Helper function to get event status
  const getEventStatus = (event) => {
    if (event.status === "closed" || event.is_closed) {
      return { label: "Closed", color: "bg-gray-500" };
    }
    if (event.status === "cancelled" || event.is_cancelled) {
      return { label: "Cancelled", color: "bg-red-500" };
    }
    const eventDate = event.event_date ? new Date(event.event_date) : null;
    if (eventDate && eventDate < new Date()) {
      return { label: "Past", color: "bg-orange-500" };
    }
    return { label: "Active", color: "bg-emerald-500" };
  };

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Section */}
      <div className="p-6 text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl">
        <h1 className="mb-2 text-2xl font-bold">
          Welcome to Events & Media Admin,{" "}
          {user?.name || user?.email || "Admin"}! 🎉
        </h1>
        <p className="text-emerald-100">
          Manage your events, conferences, trade missions, and media content
          from here.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            onClick={stat.onClick}
            className={`p-6 bg-white rounded-xl border shadow-sm border-gray-200/50 transition-all ${
              stat.onClick
                ? "cursor-pointer hover:shadow-md hover:border-emerald-300"
                : ""
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {stat.title}
                </p>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon size={24} className="text-white" />
              </div>
            </div>
            {/* <div className="flex items-center mt-4">
              <span
                className={`text-sm font-medium ${
                  stat.changeType === "positive"
                    ? "text-emerald-600"
                    : stat.changeType === "negative"
                    ? "text-red-600"
                    : "text-gray-500"
                }`}
              >
                {stat.change}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {stat.changeType === "neutral" ? "no change" : "from last month"}
              </span>
            </div> */}
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              disabled={!action.available}
              className={`flex flex-col items-center p-4 rounded-lg border transition-colors ${
                action.available
                  ? "border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 cursor-pointer"
                  : "border-gray-100 cursor-not-allowed opacity-60"
              }`}
            >
              <div className={`p-3 rounded-lg ${action.color} mb-3`}>
                <action.icon size={24} />
              </div>
              <span
                className={`text-sm font-medium ${
                  action.available ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {action.title}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Recent Events
          </h2>
          {recentEvents.length > 0 && (
            <button
              onClick={() => navigate("/admin/media/events")}
              className="flex gap-1 items-center text-sm font-medium text-emerald-600 transition-colors hover:text-emerald-700"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
          <div className="space-y-3">
            {recentEvents.length === 0 ? (
              <div className="flex justify-center items-center p-8 text-center">
                <div>
                  <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No events yet</p>
                  <p className="text-sm text-gray-400">
                    Create your first event to get started
                  </p>
                </div>
              </div>
            ) : (
              recentEvents.map((event, index) => {
                const status = getEventStatus(event);
                const eventSlug = event.slug || event.event_slug;
                return (
                  <div
                    key={event.id || eventSlug || index}
                    onClick={() =>
                      eventSlug && navigate(`/admin/media/events/view/${eventSlug}`,{
                        state:{eventData:event}
                      })
                    }
                    className={`group relative overflow-hidden bg-white border rounded-lg transition-all duration-300 ${
                      eventSlug
                        ? "border-gray-200 cursor-pointer hover:border-emerald-300 hover:shadow-md"
                        : "border-gray-200"
                    }`}
                  >
                    <div className="flex gap-4 p-4">
                      {/* Event Thumbnail */}
                      {event.thumbnail ? (
                        <div className="overflow-hidden flex-shrink-0 w-20 h-20 bg-gray-100 rounded-lg">
                          <img
                            src={event.thumbnail}
                            alt={getEventTitle(event)}
                            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-110"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentElement.classList.add(
                                "bg-gradient-to-br",
                                "from-emerald-100",
                                "to-blue-100"
                              );
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-shrink-0 justify-center items-center w-20 h-20 bg-gradient-to-br from-emerald-100 to-blue-100 rounded-lg">
                          <Calendar className="w-8 h-8 text-emerald-600" />
                        </div>
                      )}

                      {/* Event Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-2 justify-between items-start mb-2">
                          <h3 className="text-sm font-bold leading-tight text-gray-900 transition-colors line-clamp-2 group-hover:text-emerald-600">
                            {getEventTitle(event)}
                          </h3>
                          <span
                            className={`flex-shrink-0 px-2 py-0.5 text-xs font-semibold text-white rounded-full ${status.color}`}
                          >
                            {status.label}
                          </span>
                        </div>

                        {/* Event Meta Info */}
                        <div className="space-y-1.5">
                          {event.event_date && (
                            <div className="flex items-center text-xs text-gray-600">
                              <Calendar className="w-3.5 h-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                              <span>{formatDate(event.event_date)}</span>
                              {event.event_time && (
                                <>
                                  <Clock className="w-3.5 h-3.5 ml-2 mr-1.5 text-gray-400 flex-shrink-0" />
                                  <span>{formatTime(event.event_time)}</span>
                                </>
                              )}
                            </div>
                          )}

                          {event.venue_name && (
                            <div className="flex items-center text-xs text-gray-600">
                              <MapPin className="w-3.5 h-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">
                                {event.venue_name}
                              </span>
                            </div>
                          )}

                          {event.event_type &&
                            Array.isArray(event.event_type) &&
                            event.event_type.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {event.event_type.slice(0, 2).map((type, idx) => (
                                  <span
                                    key={idx}
                                    className="px-1.5 py-0.5 text-[10px] font-medium text-purple-700 bg-purple-50 rounded capitalize"
                                  >
                                    {type.replace("_", " ")}
                                  </span>
                                ))}
                                {event.event_type.length > 2 && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-100 rounded">
                                    +{event.event_type.length - 2}
                                  </span>
                                )}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* View Icon */}
                      {eventSlug && (
                        <div className="flex flex-shrink-0 items-center">
                          <Eye className="w-4 h-4 text-gray-400 transition-colors group-hover:text-emerald-600" />
                        </div>
                      )}
                    </div>

                    {/* Hover Effect Border */}
                    {eventSlug && (
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-500 transition-all duration-300 group-hover:w-full" />
                    )}
                  </div>
                );
              })
            )}
        </div>
      </div>
    </div>
  );
}
